import get from 'lodash.get';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var TableContainer = function TableContainer(_ref) {
  var _classnames;

  var className = _ref.className,
      responsive = _ref.responsive,
      labels = _ref.labels,
      sortable = _ref.sortable,
      fixed = _ref.fixed,
      children = _ref.children,
      rest = _objectWithoutPropertiesLoose(_ref, ["className", "responsive", "labels", "sortable", "fixed", "children"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, rest, {
    className: "ln-c-table-container"
  }), /*#__PURE__*/React.createElement("table", {
    className: classnames(className, 'ln-c-table', (_classnames = {}, _classnames["ln-c-table--responsive@" + responsive] = typeof responsive === 'string', _classnames['ln-c-table--responsive@md'] = typeof responsive !== 'string' && responsive, _classnames['ln-c-table--no-labels'] = !labels, _classnames['ln-c-table--sorted'] = sortable, _classnames['ln-c-table--fixed'] = fixed, _classnames))
  }, children));
};

TableContainer.propTypes = {
  className: PropTypes.string,
  responsive: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  labels: PropTypes.bool,
  sortable: PropTypes.bool,
  fixed: PropTypes.bool,
  children: PropTypes.node
};
TableContainer.defaultProps = {
  className: undefined,
  responsive: false,
  labels: true,
  sortable: false,
  fixed: false,
  children: undefined
};
TableContainer.displayName = 'TableContainer';

var TableCaption = function TableCaption(_ref) {
  var children = _ref.children,
      className = _ref.className,
      visuallyHidden = _ref.visuallyHidden,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className", "visuallyHidden"]);

  return /*#__PURE__*/React.createElement("caption", _extends({}, rest, {
    className: classnames('ln-c-table__caption', className, visuallyHidden && 'ln-u-visually-hidden')
  }), children);
};

TableCaption.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  visuallyHidden: PropTypes.bool
};
TableCaption.defaultProps = {
  className: undefined,
  visuallyHidden: false
};
TableCaption.displayName = 'TableCaption';

var TableHeader = function TableHeader(_ref) {
  var children = _ref.children,
      sortLabel = _ref.sortLabel,
      className = _ref.className,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "sortLabel", "className"]);

  return /*#__PURE__*/React.createElement("thead", _extends({}, rest, {
    className: classnames(className, 'ln-c-table__header'),
    "aria-label": sortLabel ? 'Sort by' : undefined
  }), children);
};

TableHeader.propTypes = {
  children: PropTypes.node,
  sortLabel: PropTypes.bool,
  className: PropTypes.string
};
TableHeader.defaultProps = {
  children: undefined,
  sortLabel: false,
  className: undefined
};
TableHeader.displayName = 'TableHeader';

var TableHeaderRow = function TableHeaderRow(_ref) {
  var children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className"]);

  return /*#__PURE__*/React.createElement("tr", _extends({}, rest, {
    className: classnames(className, 'ln-c-table__row', 'ln-c-table__header-row')
  }), children);
};

TableHeaderRow.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};
TableHeaderRow.defaultProps = {
  children: undefined,
  className: undefined
};
TableHeaderRow.displayName = 'TableHeaderRow';

var TableHeaderCell = function TableHeaderCell(_ref) {
  var _classnames;

  var children = _ref.children,
      className = _ref.className,
      align = _ref.align,
      onSort = _ref.onSort,
      sortDirection = _ref.sortDirection,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className", "align", "onSort", "sortDirection"]);

  return /*#__PURE__*/React.createElement("th", _extends({}, rest, {
    className: classnames('ln-c-table__header-cell', className, (_classnames = {
      'ln-c-table__header-cell--sortable': !!onSort
    }, _classnames["ln-c-table__header-cell--text-align-" + align] = align, _classnames))
  }), onSort ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: classnames('ln-c-table__sort-button', {
      'is-active': !!sortDirection,
      'is-ascending': sortDirection === 'ascending'
    }),
    onClick: function onClick() {
      return onSort(sortDirection === 'ascending');
    }
  }, children, /*#__PURE__*/React.createElement("span", {
    className: "ln-c-table__sort-text",
    "aria-label": "Sort by"
  })) : children);
};

TableHeaderCell.propTypes = {
  children: PropTypes.node,
  onSort: PropTypes.func,
  sortDirection: PropTypes.oneOf(['ascending', 'descending']),
  className: PropTypes.string,
  align: PropTypes.oneOf(['left', 'right', 'center'])
};
TableHeaderCell.defaultProps = {
  children: undefined,
  onSort: undefined,
  sortDirection: undefined,
  className: undefined,
  align: undefined
};
TableHeaderCell.displayName = 'TableHeaderCell';

var TableBody = function TableBody(_ref) {
  var children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className"]);

  return /*#__PURE__*/React.createElement("tbody", _extends({}, rest, {
    className: classnames(className, 'ln-c-table__body')
  }), children);
};

TableBody.propTypes = {
  children: PropTypes.node,
  className: PropTypes.node
};
TableBody.defaultProps = {
  children: undefined,
  className: undefined
};
TableBody.displayName = 'TableBody';

var TableRow = function TableRow(_ref) {
  var children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className"]);

  return /*#__PURE__*/React.createElement("tr", _extends({}, rest, {
    className: classnames(className, 'ln-c-table__row')
  }), children);
};

TableRow.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};
TableRow.defaultProps = {
  children: undefined,
  className: undefined
};
TableRow.displayName = 'TableRow';

var TableCell = function TableCell(_ref) {
  var _classnames;

  var children = _ref.children,
      label = _ref.label,
      className = _ref.className,
      align = _ref.align,
      lastWhenStacked = _ref.lastWhenStacked,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "label", "className", "align", "lastWhenStacked"]);

  return /*#__PURE__*/React.createElement("td", _extends({}, rest, {
    className: classnames(className, 'ln-c-table__cell', (_classnames = {}, _classnames["ln-c-table__cell--text-align-" + align] = align, _classnames), lastWhenStacked && 'ln-c-table__cell--last-when-stacked'),
    "data-label": label
  }), children);
};

TableCell.propTypes = {
  /** Content to render within the cell */
  children: PropTypes.node,

  /** Label associated with the cell - used when displayed in a responsive "stacked" view */
  label: PropTypes.string,

  /** Add custom classes e.g. for styling purposes */
  className: PropTypes.string,

  /** Horizontally aligns the table cell contents */
  align: PropTypes.oneOf(['left', 'right', 'center']),

  /** Displays at the bottom in responsive mode when "stack" view showing (e.g. on mobile) */
  lastWhenStacked: PropTypes.bool
};
TableCell.defaultProps = {
  children: undefined,
  label: undefined,
  className: undefined,
  align: undefined,
  lastWhenStacked: false
};
TableCell.displayName = 'TableCell';

var sortAlphabetically = function sortAlphabetically(accessor, sortValue, ascending) {
  return function (a, b) {
    var valueA = accessor(a)[sortValue];
    var valueB = accessor(b)[sortValue];

    if (valueA < valueB) {
      return ascending ? -1 : 1;
    }

    if (valueA > valueB) {
      return ascending ? 1 : -1;
    }

    return 0;
  };
};

var sort = function sort(_ref) {
  var data = _ref.data,
      sorted = _ref.sorted;

  if (Object.keys(sorted).length > 0) {
    var sortFunction = typeof sorted.sortValue === 'function' ? sorted.sortValue(sorted.accessor, sorted.ascending) : sortAlphabetically(sorted.accessor, sorted.sortValue, sorted.ascending);
    return {
      data: data.sort(sortFunction)
    };
  }

  return {};
};

var sortDataHandler = function sortDataHandler(_ref2) {
  var setSorted = _ref2.setSorted;
  return function (sortOptions) {
    return setSorted(sortOptions);
  };
};

var DEFAULT_CELL_DATA_PROPERTY = 'value';

var buildAccessor = function buildAccessor(accessor) {
  if (typeof accessor === 'function') {
    return accessor;
  }

  if (typeof accessor === 'string') {
    return function (row) {
      var _ref;

      return _ref = {}, _ref[DEFAULT_CELL_DATA_PROPERTY] = row[accessor], _ref;
    };
  }

  return function (row) {
    return row;
  };
};

var buildAccessors = function buildAccessors(columns) {
  return columns.reduce(function (acc, curr) {
    acc[curr.name] = buildAccessor(curr.accessor);
    return acc;
  }, {});
};

var buildColClassName = function buildColClassName(columns) {
  return columns.reduce(function (classNames, column) {
    var _Object$assign;

    return Object.assign({}, classNames, (_Object$assign = {}, _Object$assign[column.name] = typeof column.className === 'object' ? column.className : {
      th: column.className,
      td: column.className
    }, _Object$assign));
  }, {});
};
/**
 * The `column` prop definition can be customised with the following options:
 *
 * - `renderHead`: `node` - what to display in the column heading, by default column name is displayed
 * - `accessor`: `func` - access deeply nested data structures or apply transformations to data should follow the format `data => ({ value: data.email.toLowerCase() })`
 * - `render`: `node` - determine how the cell contents are output, receives props defined by the `accessor` function so may contain multiple pieces of row data e.g. `props => <span><b>{props.label}</b> {props.unit}</span>`
 * - `sort`: `string` or `func` - String value that determines what value returned by the `accessor` has alphabetical sorting applied to it, alternatively passing a function allows for custom sorting (`accessor`: `func`, `ascending`: bool) => [sort function](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort)
 * - `sortable`: `bool` - determines whether this column is sortable or not. Overrides top level `<Table />` prop `sortable`
 * - `className`: `string` or `obj` - adds a custom class to the cells within that column, can provide an object (`{ th: 'header', td: 'body' }`) to have different classes for header & body cells.
 *
 */


var Table = function Table(_ref2) {
  var columns = _ref2.columns,
      unsortedData = _ref2.data,
      className = _ref2.className,
      responsive = _ref2.responsive,
      labels = _ref2.labels,
      sortable = _ref2.sortable,
      caption = _ref2.caption,
      visuallyHiddenCaption = _ref2.visuallyHiddenCaption,
      fixed = _ref2.fixed,
      rowKey = _ref2.rowKey,
      rest = _objectWithoutPropertiesLoose(_ref2, ["columns", "data", "className", "responsive", "labels", "sortable", "caption", "visuallyHiddenCaption", "fixed", "rowKey"]);

  var _useState = useState({}),
      sorted = _useState[0],
      setSorted = _useState[1];

  var accessors = buildAccessors(columns);
  var colClassNames = buildColClassName(columns);
  var isSortable = sortable || columns.filter(function (c) {
    return c.sortable;
  }).length > 0;
  var sortData = sortDataHandler({
    setSorted: setSorted
  });

  var _sort = sort({
    data: unsortedData,
    sorted: sorted
  }),
      sortedData = _sort.data;

  var data = sortedData || unsortedData;
  return /*#__PURE__*/React.createElement(TableContainer, _extends({
    className: className,
    responsive: responsive,
    labels: labels,
    sortable: sortable
  }, rest), caption && /*#__PURE__*/React.createElement(TableCaption, {
    visuallyHidden: visuallyHiddenCaption
  }, caption), /*#__PURE__*/React.createElement(TableHeader, {
    sortLabel: responsive && isSortable
  }, /*#__PURE__*/React.createElement(TableHeaderRow, null, columns.map(function (column) {
    var sortDirection;

    if (sorted.column === column.name) {
      sortDirection = sorted.ascending ? 'ascending' : 'descending';
    }

    return /*#__PURE__*/React.createElement(TableHeaderCell, {
      key: column.name,
      sortDirection: sortDirection,
      onSort: column.sortable || column.sortable === undefined && sortable ? function (ascendingActive) {
        return sortData({
          column: column.name,
          accessor: accessors[column.name],
          ascending: !ascendingActive,
          sortValue: column.sort || DEFAULT_CELL_DATA_PROPERTY
        });
      } : undefined,
      align: column.align,
      className: colClassNames[column.name].th
    }, column.renderHead === undefined ? column.name : column.renderHead);
  }))), /*#__PURE__*/React.createElement(TableBody, null, data.map(function (row, index) {
    return /*#__PURE__*/React.createElement(TableRow, {
      key: get(row, rowKey, index)
    }, columns.map(function (column) {
      var cellData = accessors[column.name](row);
      return /*#__PURE__*/React.createElement(TableCell, {
        key: column.name,
        align: column.align,
        className: colClassNames[column.name].td,
        label: responsive && labels && !column.hideLabel ? column.name : undefined,
        lastWhenStacked: responsive && column.lastWhenStacked
      }, column.render ? column.render(cellData) : cellData[DEFAULT_CELL_DATA_PROPERTY]);
    }));
  })));
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    renderHead: PropTypes.node,
    accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    sort: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    sortable: PropTypes.bool,
    render: PropTypes.func,

    /** Displays this cell at the bottom when stack view presented - requires top-level `responsive` prop to be enabled */
    lastWhenStacked: PropTypes.bool
  })).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  className: PropTypes.string,
  responsive: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  labels: PropTypes.bool,
  sortable: PropTypes.bool,
  caption: PropTypes.node,
  visuallyHiddenCaption: PropTypes.bool,
  fixed: PropTypes.bool,

  /** This supports deep gets within the data, e.g. 'property.id' */
  rowKey: PropTypes.string
};
Table.defaultProps = {
  className: undefined,
  responsive: false,
  labels: true,
  sortable: false,
  caption: undefined,
  visuallyHiddenCaption: false,
  fixed: false,
  rowKey: 'id'
};
Table.displayName = 'Table';
export { Table, Table as TableComponent, TableContainer, TableBody, TableCaption, TableCell, TableHeader, TableHeaderCell, TableHeaderRow, TableRow };